/**
 * cockpit_audio_context — 콕핏 전체가 함께 쓰는 단일 AudioContext.
 *
 * 브라우저 자동재생 정책 때문에, 사용자 제스처 없이 만든 AudioContext 는 suspended
 * 상태로 시작해 아무 소리도 나지 않는다. 주행 중에 "소리를 켜려면 화면을 클릭하세요"
 * 같은 안내를 띄우는 건 원격주행에서 최악이므로, **이미 존재하는 사용자 조작**
 * (로그인 버튼, 차량 선택)에서 미리 열어둔다.
 *
 * 컨텍스트를 하나로 모으는 이유: 브라우저마다 동시에 열 수 있는 AudioContext 수가
 * 제한되어 있고, 제스처로 한 번 풀어둔 컨텍스트를 재사용해야 이후 소리들이 모두
 * 자동재생 정책을 통과한다.
 */

type AudioContextCtor = typeof AudioContext;

let sharedContext: AudioContext | null = null;

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === "undefined") {
    return null;
  }
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * 사용자 제스처 핸들러 안에서 호출한다(로그인 클릭, 차량 선택 등).
 *
 * 제스처 안에서 만든 컨텍스트는 곧바로 running 으로 시작하고, 이미 만들어져 있으면
 * resume() 으로 되살린다. 지원하지 않는 환경에서는 조용히 아무것도 하지 않는다.
 */
export function unlockCockpitAudio(): void {
  const context = getCockpitAudioContext();
  if (context && context.state === "suspended") {
    void context.resume().catch(() => {});
  }
}

/**
 * 진짜 사용자 제스처가 처음 발생할 때 오디오를 푸는 전역 안전망을 설치한다.
 *
 * 로그인/차량 선택 핸들러에서 unlockCockpitAudio() 를 부르는 것만으로는 부족하다 —
 * 게이밍 휠로 로그인하면 그 핸들러는 **WebHID 입력 리포트**에서 실행되는데, 브라우저는
 * 이를 사용자 제스처(transient activation)로 인정하지 않는다. 그래서 resume() 이 조용히
 * 실패하고 컨텍스트가 suspended 로 남아 주행 사운드가 아예 나지 않았다.
 * (같은 이유로 휠 페달은 requestFullscreen 도 트리거하지 못한다.)
 *
 * 실제 포인터/키 입력이 한 번이라도 있으면 그때 풀고 리스너를 뗀다.
 */
export function installCockpitAudioUnlock(): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const events = ["pointerdown", "keydown", "touchstart"] as const;

  const handler = () => {
    unlockCockpitAudio();
    // 이미 풀렸으면 더 들을 이유가 없다.
    if (sharedContext?.state === "running") {
      remove();
    }
  };

  const remove = () => {
    events.forEach((name) => window.removeEventListener(name, handler));
  };

  events.forEach((name) =>
    window.addEventListener(name, handler, { passive: true }),
  );

  return remove;
}

/**
 * 공용 컨텍스트를 얻는다(없으면 만든다).
 *
 * 제스처 밖에서 처음 부르면 suspended 로 시작할 수 있다 — 그래도 객체는 유효하므로,
 * 이후 unlockCockpitAudio() 가 제스처에서 불리면 그대로 살아난다.
 */
export function getCockpitAudioContext(): AudioContext | null {
  if (sharedContext && sharedContext.state !== "closed") {
    return sharedContext;
  }

  const Ctor = getAudioContextCtor();
  if (!Ctor) {
    return null; // jsdom·미지원 브라우저
  }

  try {
    sharedContext = new Ctor();
  } catch {
    sharedContext = null;
  }
  return sharedContext;
}

/** 테스트용 — 모듈 상태를 비운다. */
export function resetCockpitAudioContextForTest(): void {
  sharedContext = null;
}
